/**
 * `AssessmentBadge` — small status pill for a `sources/` artifact
 * representing the lawyer's `.info` assessment state.
 *
 * Three states (mission-ingest-implementation-plan.md §13.1(a)):
 *  - `pending`   — amber chip "needs assessment" (the at-a-glance todo for the lawyer)
 *  - `assessed`  — slate chip with the lawyer's role (open free-text; the seeds in
 *                  `SUGGESTED_ROLE_SEEDS` are convenience only); optional weight dot
 *                  (red high · amber medium · slate low) to the right
 *  - `declined`  — muted chip "skipped" (the deliberate "decide later")
 *
 * Returns `null` if `status` is absent — the source-row caller passes the
 * badge data straight from the catalog (`sources/_index.json` once
 * `index-builder` censuses `.info` status into it — pending runtime dep,
 * see `source-info-viewer-lexruntime-dep-handoff-2026-06-27.md` §2).
 * Until that ships, the catalog row has no status field and the badge is
 * absent — by design, no false signal.
 *
 * Pure presentational; no data fetch. The opener / panel mount lives in
 * the row component that hosts the badge.
 */

import { AlertCircle, CheckCircle2, MinusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SourceAssessmentStatus, SourceAssessmentWeight } from "@/types/gateway";
import { cn } from "@/lib/utils";
import { badgeLabel } from "./assessment-helpers";

export interface AssessmentBadgeProps {
  status: SourceAssessmentStatus | undefined | null;
  /** Open free-text. Rendered verbatim when `assessed`. */
  role?: string;
  weight?: SourceAssessmentWeight;
  /** Optional click handler — when wired, the badge becomes a button. */
  onClick?: () => void;
  /** Compact (no label, icon-only) for very narrow source rows. */
  compact?: boolean;
}

export function AssessmentBadge({
  status,
  role,
  weight,
  onClick,
  compact,
}: AssessmentBadgeProps) {
  const { t } = useTranslation();
  if (!status) return null;

  const variant = VARIANTS[status];
  const Icon = variant.icon;
  const tooltip = badgeLabel(status, role, t);
  const label = compact ? undefined : tooltip;
  const interactive = Boolean(onClick);

  const body = (
    <span
      data-testid="source-assessment-badge"
      data-status={status}
      data-role={role || undefined}
      data-weight={weight || undefined}
      title={tooltip}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none tracking-tight",
        variant.chip,
        interactive &&
          "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40",
      )}
    >
      <Icon size={11} aria-hidden className="shrink-0" />
      {label && <span className="max-w-[10rem] truncate">{label}</span>}
      {weight && (
        <span
          data-testid="source-assessment-weight-dot"
          aria-hidden
          className={cn("ml-0.5 h-1.5 w-1.5 shrink-0 rounded-full", WEIGHT_DOT[weight])}
        />
      )}
    </span>
  );

  if (!interactive) return body;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={tooltip}
      className="inline-flex shrink-0 items-center rounded-full"
    >
      {body}
    </button>
  );
}

const VARIANTS: Record<
  SourceAssessmentStatus,
  { icon: typeof AlertCircle; chip: string }
> = {
  pending: {
    icon: AlertCircle,
    chip:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200",
  },
  assessed: {
    icon: CheckCircle2,
    chip:
      "bg-slate-100 text-slate-700 dark:bg-slate-800/70 dark:text-slate-200",
  },
  declined: {
    icon: MinusCircle,
    chip:
      "bg-slate-50 text-slate-500 dark:bg-slate-900/40 dark:text-slate-400",
  },
};

const WEIGHT_DOT: Record<SourceAssessmentWeight, string> = {
  high: "bg-red-500",
  medium: "bg-amber-500",
  low: "bg-slate-400",
};
